// Standard Reports PDF
// --------------------
// Builds the "Standard Reports" as a TRUE PDF using jsPDF (window.jspdf, loaded via
// <script> in index.html — same global actionSheetPdf.ts / authorityDoc.ts use), and
// opens it in a new tab for preview + printing (download fallback if the popup is
// blocked). Four reports (any one, or all together):
//   1. Number of Shelters            3. Active Users
//   2. Children Assisted by Category 4. Number of Requests
//
// Letterhead: the Zephyr logo (left) + "Zephyr Education Limited", then the report
// name, then the filter selection as label/value pairs across the page. Tables are
// hand-drawn (the app has no jspdf-autotable). The row/table math lives in
// services/standardReports.js (pure, unit-tested); this file is layout only, except
// reportTitle / filterSummaryPairs which ARE unit-tested.

import { STANDARD_CATEGORIES } from "../services/standardReports";
import { logger } from "./logger";

export type StandardReportKind =
  | "shelters"
  | "children"
  | "users"
  | "requests"
  | "statistics"
  | "no_login"
  | "all";

export interface StandardReportFilters {
  fromDate: string;
  toDate: string;
  state: string; // "All" or a state code
  customerType: string; // "" = All, else a customer-type category
  category: string; // "all" or a category key
  groupBySub: boolean;
  decision: string; // approved | declined | both
}

export interface ShelterCountReport {
  rows: Array<{ state: string; col1: number; col2: number }>;
  totals: { col1: number; col2: number };
}
export interface CategoryStateRow {
  state: string;
  children: number;
  cost: number;
  subs: Record<string, { children: number; cost: number }>;
}
export interface CategoryReport {
  rows: CategoryStateRow[];
  totals: { children: number; cost: number };
}
export type CategoryReportMap = Record<string, CategoryReport>;
export interface UsersReport {
  rows: Array<{
    state: string;
    logins: number;
    signedIn: number;
    activeShelters: number;
    sheltersSignedIn: number;
  }>;
  totals: { logins: number; signedIn: number; activeShelters: number; sheltersSignedIn: number };
}
export interface NoLoginReport {
  rows: Array<{ state: string; services: Array<{ name: string; code: string }> }>;
  total: number;
}
export interface RequestsReport {
  rows: Array<{ state: string; count: number; cost: number }>;
  totals: { count: number; cost: number };
}

// The "Statistics Summary" report (mirrors the Statistics dashboard; Approved-only).
export interface StatisticsReportData {
  totals: {
    approvedReqs: number;
    approvedChildren: number;
    approvedValue: number;
    processedReqs: number;
    totalReqs: number;
    avgChildren: number;
    avgPerReq: number;
    avgPerChild: number;
    feesTotal: number;
    uniformsTotal: number;
    shoesTotal: number;
    devicesTotal: number;
    stationeryTotal: number;
  };
  byStatus: Array<{ status: string; count: number }>;
  byCategory: Array<{ key: string; label: string; count: number; children: number; value: number }>;
  byState: Array<{ state: string; count: number; children: number; value: number }>;
  trend: Array<{ key: string; count: number; children: number; value: number }>;
  byShelter: Array<{
    name: string;
    state: string;
    requests: number;
    children: number;
    fees: number;
    uniforms: number;
    shoes: number;
    devices: number;
    stationery: number;
    value: number;
  }>;
}

export interface StandardReportInput {
  report: StandardReportKind;
  filters: StandardReportFilters;
  shelterReport?: ShelterCountReport;
  catReport?: CategoryReportMap;
  shownCategoryKeys?: string[];
  usersReport?: UsersReport;
  noLoginReport?: NoLoginReport;
  requestsReport?: RequestsReport;
  statisticsReport?: StatisticsReportData;
  respHours?: number | null; // Response Time card (avg business hours); null = "-"
}

type RGB = [number, number, number];

// ── Pure helpers (unit-tested) ───────────────────────────────────────────────

export function reportTitle(report: StandardReportKind): string {
  if (report === "shelters") return "Number of Shelters";
  if (report === "children") return "Children Assisted by Category";
  if (report === "users") return "Active Users";
  if (report === "requests") return "Number of Requests";
  if (report === "statistics") return "Statistics Summary";
  if (report === "no_login") return "Shelters Without an Activated Login";
  return "Standard Reports";
}

// dd/mm/yyyy for an ISO yyyy-mm-dd, or a fallback for blank.
function fmtDate(iso: string, blank: string): string {
  if (!iso) return blank;
  const p = iso.slice(0, 10).split("-");
  if (p.length !== 3) return iso;
  return `${p[2]}/${p[1]}/${p[0]}`;
}

const CATEGORY_LABEL: Record<string, string> = Object.fromEntries(
  STANDARD_CATEGORIES.map((c) => [c.key, c.label])
);
const DECISION_LABEL: Record<string, string> = {
  approved: "Approved",
  declined: "Declined",
  both: "Both",
};

// The filter selection as label/value pairs, laid out across the page. Only the
// fields a given report actually honours are included (Report 1 / 3 = State + dates).
export function filterSummaryPairs(
  report: StandardReportKind,
  f: StandardReportFilters
): Array<{ label: string; value: string }> {
  const pairs: Array<{ label: string; value: string }> = [];
  pairs.push({ label: "From", value: fmtDate(f.fromDate, "earliest") });
  pairs.push({ label: "To", value: fmtDate(f.toDate, "today") });
  pairs.push({ label: "State", value: f.state || "All" });
  // Statistics honours only From/To/State and is always Approved (committed) spend.
  if (report === "statistics") {
    pairs.push({ label: "Decision", value: "Approved" });
    return pairs;
  }
  const showCat = report === "children" || report === "all";
  const showReqish = report === "children" || report === "requests" || report === "all";
  if (showReqish) pairs.push({ label: "DFV Service Type", value: f.customerType || "All" });
  if (showCat) {
    pairs.push({
      label: "Category",
      value: f.category === "all" ? "All Items" : CATEGORY_LABEL[f.category] || f.category,
    });
  }
  if (showReqish)
    pairs.push({ label: "Decision", value: DECISION_LABEL[f.decision] || f.decision });
  if (showCat) pairs.push({ label: "Sub-category", value: f.groupBySub ? "Yes" : "No" });
  return pairs;
}

function money2(n: number): string {
  return `$${Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

// ── jsPDF plumbing ───────────────────────────────────────────────────────────

function waitForJsPDF(): Promise<boolean> {
  return new Promise(function (resolve) {
    let attempts = 0;
    const check = function () {
      if (window.jspdf?.jsPDF) return resolve(true);
      if (++attempts > 50) return resolve(false);
      setTimeout(check, 100);
    };
    check();
  });
}

// Fetch the same-origin logo as a PNG data URL, or null (degrade to no logo).
async function loadLogoDataUrl(): Promise<string | null> {
  try {
    const res = await fetch("/zephyr-favicon-512.png");
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise<string | null>((resolve) => {
      const fr = new FileReader();
      fr.onload = () => resolve(typeof fr.result === "string" ? fr.result : null);
      fr.onerror = () => resolve(null);
      fr.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

export async function openStandardReportPdf(input: StandardReportInput): Promise<boolean> {
  const ready = await waitForJsPDF();
  if (!ready) {
    logger.warn("jsPDF not loaded — cannot build Standard Report PDF");
    return false;
  }
  const logo = await loadLogoDataUrl();

  const jsPDF = window.jspdf!.jsPDF;
  const doc = new jsPDF({ unit: "mm", format: "a4" });

  const pageW = 210;
  const margin = 16;
  const contentW = pageW - margin * 2;
  const bottom = 285;
  let y = 16;

  const teal: RGB = [0, 105, 92];
  const dark: RGB = [26, 46, 43];
  const muted: RGB = [96, 125, 121];
  const white: RGB = [255, 255, 255];
  const zebra: RGB = [245, 247, 247];
  const totalFill: RGB = [207, 224, 222]; // light teal band for TOTAL rows
  const totalInk: RGB = [0, 0, 0]; // black text on the light TOTAL band

  const setInk = (c: RGB) => doc.setTextColor(c[0], c[1], c[2]);
  const setFill = (c: RGB) => doc.setFillColor(c[0], c[1], c[2]);

  function checkPage(needed: number) {
    if (y + needed > bottom) {
      doc.addPage();
      y = 16;
    }
  }

  // ── Letterhead ──
  if (logo) {
    try {
      doc.addImage(logo, "PNG", margin, y, 16, 16);
    } catch (_e) {
      // ignore a bad image — carry on without the logo
    }
  }
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  setInk(teal);
  doc.text("Zephyr Education Limited", margin + (logo ? 20 : 0), y + 10);
  y += 20;

  // ── Report name ──
  doc.setFont("helvetica", "bold");
  doc.setFontSize(13);
  setInk(dark);
  doc.text(reportTitle(input.report), margin, y);
  y += 7;

  // ── Filter selection — label/value pairs across the page ──
  const pairs = filterSummaryPairs(input.report, input.filters);
  const perRow = 3;
  const cellW = contentW / perRow;
  doc.setFontSize(9);
  let rowY = y;
  pairs.forEach((p, i) => {
    const col = i % perRow;
    if (col === 0 && i > 0) rowY += 5.5;
    const x = margin + col * cellW;
    doc.setFont("helvetica", "bold");
    setInk(dark);
    doc.text(`${p.label}:`, x, rowY);
    const lw = doc.getTextWidth(`${p.label}: `);
    doc.setFont("helvetica", "normal");
    setInk(muted);
    doc.text(String(p.value), x + lw, rowY);
  });
  y = rowY + 5;
  doc.setDrawColor(teal[0], teal[1], teal[2]);
  doc.setLineWidth(0.5);
  doc.line(margin, y, margin + contentW, y);
  y += 7;

  // ── Table primitives ───────────────────────────────────────────────────────
  const ROW_H = 7;
  function tableHeader(headers: string[], xs: number[], aligns: string[]) {
    checkPage(ROW_H + 4);
    setFill(teal);
    doc.rect(margin, y, contentW, ROW_H, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    setInk(white);
    headers.forEach((h, i) => {
      const align = aligns[i] || "left";
      doc.text(h, xs[i], y + 4.8, { align: align as "left" | "right" });
    });
    y += ROW_H;
  }
  function tableRow(
    cells: string[],
    xs: number[],
    aligns: string[],
    opts: { fill?: RGB; ink?: RGB; bold?: boolean; indentFirst?: boolean } = {}
  ) {
    checkPage(ROW_H + 2);
    if (opts.fill) {
      setFill(opts.fill);
      doc.rect(margin, y, contentW, ROW_H, "F");
    }
    doc.setFont("helvetica", opts.bold ? "bold" : "normal");
    doc.setFontSize(9);
    setInk(opts.ink || dark);
    cells.forEach((c, i) => {
      const align = aligns[i] || "left";
      const x = align === "right" ? xs[i] : xs[i] + (i === 0 && opts.indentFirst ? 6 : 0);
      doc.text(String(c), x, y + 4.8, { align: align as "left" | "right" });
    });
    y += ROW_H;
  }
  function sectionHeading(text: string) {
    checkPage(12);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    setInk(teal);
    doc.text(text, margin, y);
    y += 6;
  }
  function note(text: string) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    setInk(muted);
    const lines = doc.splitTextToSize(text, contentW);
    doc.text(lines, margin, y);
    y += lines.length * 4 + 2;
  }

  const showHeadings = input.report === "all";

  // ── Report 1 — Number of Shelters ──
  function drawShelterReport() {
    const rep = input.shelterReport;
    if (!rep) return;
    if (showHeadings) sectionHeading("Number of Shelters");
    note(
      "Services onboarded on or before the From date (start) vs the To date (end). Blank To = today; blank From = 0 at start. Deactivated / no-onboarding-date services excluded."
    );
    const xs = [margin + 2, margin + contentW - 44, margin + contentW - 2];
    const aligns = ["left", "right", "right"];
    tableHeader(["State", "At start date", "At end date"], xs, aligns);
    if (rep.rows.length === 0) {
      tableRow(["No services with an onboarding date.", "", ""], xs, aligns, { fill: white });
    } else {
      rep.rows.forEach((r, i) => {
        tableRow([r.state, String(r.col1), String(r.col2)], xs, aligns, {
          fill: i % 2 === 0 ? white : zebra,
        });
      });
    }
    tableRow(["TOTAL", String(rep.totals.col1), String(rep.totals.col2)], xs, aligns, {
      fill: totalFill,
      ink: totalInk,
      bold: true,
    });
    y += 8;
  }

  // ── Report 2 — Children Assisted by Category ──
  function drawCategoryReport() {
    if (!input.catReport) return;
    if (showHeadings) sectionHeading("Children Assisted by Category");
    const xs = [margin + 2, margin + contentW - 96, margin + contentW - 46, margin + contentW - 2];
    const aligns = ["left", "right", "right", "right"];
    for (const key of input.shownCategoryKeys || []) {
      const rep = input.catReport[key];
      if (!rep) continue;
      checkPage(ROW_H * 2 + 10);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
      setInk(dark);
      doc.text(CATEGORY_LABEL[key] || key, margin, y);
      y += 5;
      tableHeader(["State", "Children", "Cost", "Avg / Child"], xs, aligns);
      if (rep.rows.length === 0) {
        tableRow(["No data", "", "", ""], xs, aligns, { fill: white });
      } else {
        rep.rows.forEach((row, i) => {
          const avg = row.children > 0 ? money2(row.cost / row.children) : "-";
          tableRow([row.state, String(row.children), money2(row.cost), avg], xs, aligns, {
            fill: i % 2 === 0 ? white : zebra,
            bold: true,
          });
          if (input.filters.groupBySub) {
            const subs = Object.entries(row.subs).sort((a, b) => b[1].cost - a[1].cost);
            subs.forEach(([name, s]) => {
              const savg = s.children > 0 ? money2(s.cost / s.children) : "-";
              tableRow([name, String(s.children), money2(s.cost), savg], xs, aligns, {
                fill: i % 2 === 0 ? white : zebra,
                ink: muted,
                indentFirst: true,
              });
            });
          }
        });
      }
      const tAvg = rep.totals.children > 0 ? money2(rep.totals.cost / rep.totals.children) : "-";
      tableRow(["TOTAL", String(rep.totals.children), money2(rep.totals.cost), tAvg], xs, aligns, {
        fill: totalFill,
        ink: totalInk,
        bold: true,
      });
      y += 6;
    }
    y += 2;
  }

  // ── Report 3 — Active Users ──
  function drawUsersReport() {
    const rep = input.usersReport;
    if (!rep) return;
    if (showHeadings) sectionHeading("Active Users");
    note(
      "Current snapshot (as of today). Active logins = login caseworker accounts that are not deactivated. Signed in = those that have logged in at least once. Active shelters = active DFV services; Shelters w/ sign-in = those with at least one signed-in login user."
    );
    const xs = [margin + 2, margin + 74, margin + 106, margin + 140, margin + contentW - 2];
    const aligns = ["left", "right", "right", "right", "right"];
    tableHeader(
      ["State", "Active logins", "Signed in", "Active shelters", "Shelters w/ sign-in"],
      xs,
      aligns
    );
    if (rep.rows.length === 0) {
      tableRow(["No active login users.", "", "", "", ""], xs, aligns, { fill: white });
    } else {
      rep.rows.forEach((r, i) => {
        tableRow(
          [
            r.state,
            String(r.logins),
            String(r.signedIn),
            String(r.activeShelters),
            String(r.sheltersSignedIn),
          ],
          xs,
          aligns,
          { fill: i % 2 === 0 ? white : zebra }
        );
      });
    }
    tableRow(
      [
        "TOTAL",
        String(rep.totals.logins),
        String(rep.totals.signedIn),
        String(rep.totals.activeShelters),
        String(rep.totals.sheltersSignedIn),
      ],
      xs,
      aligns,
      { fill: totalFill, ink: totalInk, bold: true }
    );
    y += 8;
  }

  // ── Report 5 — Shelters Without an Activated Login ──
  function drawNoLoginReport() {
    const rep = input.noLoginReport;
    if (!rep) return;
    if (showHeadings) sectionHeading("Shelters Without an Activated Login");
    note(
      "Active DFV services where no login user has signed in yet (nobody at the service has logged in). Listed by state."
    );
    const xs = [margin + 2, margin + contentW - 2];
    const aligns = ["left", "right"];
    if (rep.rows.length === 0) {
      tableRow(["Every active service has a signed-in user.", ""], xs, aligns, { fill: white });
      y += 8;
      return;
    }
    for (const grp of rep.rows) {
      checkPage(ROW_H * 2 + 6);
      tableRow([`${grp.state} (${grp.services.length})`, ""], xs, aligns, {
        fill: totalFill,
        ink: totalInk,
        bold: true,
      });
      grp.services.forEach((s, i) => {
        tableRow([s.name, s.code], xs, aligns, {
          fill: i % 2 === 0 ? white : zebra,
          indentFirst: true,
        });
      });
    }
    tableRow(["TOTAL services without a signed-in user", String(rep.total)], xs, aligns, {
      fill: totalFill,
      ink: totalInk,
      bold: true,
    });
    y += 8;
  }

  // ── Report 4 — Number of Requests ──
  function drawRequestsReport() {
    const rep = input.requestsReport;
    if (!rep) return;
    if (showHeadings) sectionHeading("Number of Requests");
    note("Submitted (non-draft) requests in the period. Cost = approved (per Decision) total.");
    const xs = [margin + 2, margin + contentW - 96, margin + contentW - 46, margin + contentW - 2];
    const aligns = ["left", "right", "right", "right"];
    tableHeader(["State", "Requests", "Total Cost", "Avg / Request"], xs, aligns);
    if (rep.rows.length === 0) {
      tableRow(["No data", "", "", ""], xs, aligns, { fill: white });
    } else {
      rep.rows.forEach((r, i) => {
        const avg = r.count > 0 ? money2(r.cost / r.count) : "-";
        tableRow([r.state, String(r.count), money2(r.cost), avg], xs, aligns, {
          fill: i % 2 === 0 ? white : zebra,
        });
      });
    }
    const gAvg = rep.totals.count > 0 ? money2(rep.totals.cost / rep.totals.count) : "-";
    tableRow(["TOTAL", String(rep.totals.count), money2(rep.totals.cost), gAvg], xs, aligns, {
      fill: totalFill,
      ink: totalInk,
      bold: true,
    });
    y += 8;
  }

  // ── Statistics Summary — headline + charts (page 1), then By-DFV-Service (page 2+) ──
  function money(n: number): string {
    return `$${Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 })}`;
  }
  function truncate(s: string, max: number): string {
    return s.length > max ? `${s.slice(0, max - 1)}…` : s;
  }
  function drawStatisticsReport() {
    const rep = input.statisticsReport;
    if (!rep) return;
    const t = rep.totals;

    // Headline stat cards (grid of 4 per row).
    const cards: Array<{ v: string; l: string; sub?: string; c: RGB }> = [
      {
        v: String(t.approvedReqs),
        l: "Total Requests Approved",
        sub: `${t.processedReqs} fully processed`,
        c: [0, 137, 123],
      },
      {
        v: String(t.approvedChildren),
        l: "Total Children Approved",
        sub: "with approved items",
        c: [21, 101, 192],
      },
      {
        v: t.approvedReqs > 0 ? t.avgChildren.toFixed(1) : "-",
        l: "Avg Children / Approved Request",
        c: [0, 105, 92],
      },
      {
        v: money(t.approvedValue),
        l: "Total Value Approved",
        sub: "committed (approved) spend",
        c: [56, 142, 60],
      },
      {
        v: t.approvedReqs > 0 ? money(t.avgPerReq) : "-",
        l: "Avg per Request Approved",
        c: [230, 81, 0],
      },
      {
        v: t.approvedChildren > 0 ? money(t.avgPerChild) : "-",
        l: "Average per Child Approved",
        c: [94, 53, 177],
      },
      {
        v: input.respHours == null ? "-" : `${input.respHours.toFixed(1)} hrs`,
        l: "Response Time",
        sub: "avg New Request, 8am–4:45pm",
        c: [0, 131, 143],
      },
    ];
    const perRow = 4;
    const gap = 3;
    const cardW = (contentW - gap * (perRow - 1)) / perRow;
    const cardH = 21;
    const nRows = Math.ceil(cards.length / perRow);
    for (let rw = 0; rw < nRows; rw++) {
      checkPage(cardH + 4);
      const rowY = y;
      for (let col = 0; col < perRow; col++) {
        const idx = rw * perRow + col;
        if (idx >= cards.length) break;
        const cd = cards[idx];
        const x = margin + col * (cardW + gap);
        doc.setDrawColor(207, 224, 222);
        doc.setLineWidth(0.3);
        setFill(white);
        doc.rect(x, rowY, cardW, cardH, "FD");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(14);
        setInk(cd.c);
        doc.text(cd.v, x + 3, rowY + 8);
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        setInk(dark);
        doc.text(doc.splitTextToSize(cd.l, cardW - 5), x + 3, rowY + 12.5);
        if (cd.sub) {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(6);
          setInk(muted);
          doc.text(truncate(cd.sub, 34), x + 3, rowY + cardH - 2.5);
        }
      }
      y = rowY + cardH + gap;
    }
    y += 4;

    // Horizontal-bar block (shared by Status / Category / State).
    const trackX = margin + 42;
    const trackW = contentW - 42 - 46; // leave room for the value text on the right
    function hbar(label: string, value: number, max: number, valueText: string, colour: RGB) {
      checkPage(8);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
      setInk(dark);
      doc.text(truncate(label, 26), margin, y + 3.5);
      doc.setFont("helvetica", "bold");
      setInk(muted);
      doc.text(valueText, margin + contentW, y + 3.5, { align: "right" });
      setFill([207, 224, 222]);
      doc.rect(trackX, y + 1.2, trackW, 2.6, "F");
      const w = max > 0 ? Math.max(value > 0 ? 1.5 : 0, (value / max) * trackW) : 0;
      if (w > 0) {
        setFill(colour);
        doc.rect(trackX, y + 1.2, w, 2.6, "F");
      }
      y += 6.5;
    }

    // Requests by Status.
    sectionHeading("Requests by Status");
    if (rep.byStatus.length === 0) note("No data.");
    for (const s of rep.byStatus) {
      hbar(s.status, s.count, t.totalReqs, String(s.count), [0, 137, 123]);
    }
    y += 3;

    // By Category — Approved.
    sectionHeading("By Category — Approved");
    const maxCat = Math.max(...rep.byCategory.map((c) => c.value), 1);
    const CAT_COLOUR: Record<string, RGB> = {
      fees: [21, 101, 192],
      uniforms: [0, 137, 123],
      shoes: [78, 52, 46],
      laptops: [55, 71, 79],
      stationery: [245, 127, 23],
    };
    for (const c of rep.byCategory) {
      const vt = `${c.children} child${c.children !== 1 ? "ren" : ""} · ${
        c.value > 0 ? money(c.value) : "no $"
      }`;
      hbar(c.label, c.value, maxCat, vt, CAT_COLOUR[c.key] || [0, 137, 123]);
    }
    y += 3;

    // By State — Approved.
    sectionHeading("By State — Approved");
    const maxState = Math.max(...rep.byState.map((s) => s.value), 1);
    if (rep.byState.length === 0) note("No data.");
    for (const s of rep.byState) {
      hbar(
        s.state || "—",
        s.value,
        maxState,
        `${s.count} req · ${s.children} ch · ${money(s.value)}`,
        [0, 105, 92]
      );
    }
    y += 3;

    // Monthly Trend — vertical bars.
    sectionHeading("Monthly Trend — Requests & Value");
    if (rep.trend.length === 0) {
      note("No data for the selected period.");
    } else {
      const chartH = 38;
      checkPage(chartH + 18);
      const n = rep.trend.length;
      const slotW = Math.min(contentW / n, 32);
      const maxV = Math.max(...rep.trend.map((tp) => tp.value), 1);
      const chartBottom = y + chartH;
      rep.trend.forEach((tp, i) => {
        const cx = margin + i * slotW + slotW / 2;
        const bh = Math.max(2, Math.round((tp.value / maxV) * chartH));
        const barW = Math.min(13, slotW * 0.5);
        setFill([0, 137, 123]);
        doc.rect(cx - barW / 2, chartBottom - bh, barW, bh, "F");
        doc.setFont("helvetica", "bold");
        doc.setFontSize(6.5);
        setInk([0, 137, 123]);
        doc.text(String(tp.count), cx, chartBottom - bh - 1.5, { align: "center" });
        doc.setFont("helvetica", "bold");
        doc.setFontSize(7);
        setInk(dark);
        const lbl = tp.key.length === 7 ? `${tp.key.slice(5)}/${tp.key.slice(2, 4)}` : tp.key;
        doc.text(lbl, cx, chartBottom + 4, { align: "center" });
        doc.setFont("helvetica", "normal");
        doc.setFontSize(6.5);
        setInk(muted);
        doc.text(money(tp.value), cx, chartBottom + 8, { align: "center" });
      });
      y = chartBottom + 13;
    }

    // ── By DFV Service — force onto its own page(s) ──
    doc.addPage();
    y = 16;
    sectionHeading("By DFV Service — Approved");
    const SH_H = 6;
    const shHeaders = [
      "DFV Service",
      "State",
      "Req",
      "Chn",
      "Fees $",
      "Unif $",
      "Shoes $",
      "Dev $",
      "Stat $",
      "Total $",
    ];
    // 10 columns on A4 portrait (12 Oct 2026: "Stat $" added, so the $ columns add up to
    // Total $). The six $ columns are ~18 mm apart — room for "$12,345.67" at 7.5 pt;
    // the service name is cut to 23 characters (~31 mm) to fit before State.
    const shX = [margin + 2, 54, 72, 84, 102, 121, 139, 157, 175, margin + contentW];
    const shAlign = [
      "left",
      "left",
      "right",
      "right",
      "right",
      "right",
      "right",
      "right",
      "right",
      "right",
    ];
    function shHeader() {
      setFill(teal);
      doc.rect(margin, y, contentW, SH_H, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(7.5);
      setInk(white);
      shHeaders.forEach((h, i) => {
        doc.text(h, shX[i], y + 4, { align: shAlign[i] as "left" | "right" });
      });
      y += SH_H;
    }
    function shRow(cells: string[], opts: { fill?: RGB; ink?: RGB; bold?: boolean } = {}) {
      if (y + SH_H > bottom) {
        doc.addPage();
        y = 16;
        shHeader();
      }
      if (opts.fill) {
        setFill(opts.fill);
        doc.rect(margin, y, contentW, SH_H, "F");
      }
      doc.setFont("helvetica", opts.bold ? "bold" : "normal");
      doc.setFontSize(7.5);
      setInk(opts.ink || dark);
      cells.forEach((c, i) => {
        doc.text(String(c), shX[i], y + 4, { align: shAlign[i] as "left" | "right" });
      });
      y += SH_H;
    }
    shHeader();
    if (rep.byShelter.length === 0) {
      shRow(["No approved requests in the selected period.", "", "", "", "", "", "", "", "", ""], {
        fill: white,
      });
    } else {
      rep.byShelter.forEach((s, i) => {
        shRow(
          [
            truncate(s.name || "—", 23),
            s.state || "",
            String(s.requests),
            String(s.children),
            s.fees > 0 ? money2(s.fees) : "-",
            s.uniforms > 0 ? money2(s.uniforms) : "-",
            s.shoes > 0 ? money2(s.shoes) : "-",
            s.devices > 0 ? money2(s.devices) : "-",
            s.stationery > 0 ? money2(s.stationery) : "-",
            money2(s.value),
          ],
          { fill: i % 2 === 0 ? white : zebra }
        );
      });
    }
    shRow(
      [
        "TOTAL",
        "",
        String(t.approvedReqs),
        String(t.approvedChildren),
        money2(t.feesTotal),
        money2(t.uniformsTotal),
        money2(t.shoesTotal),
        money2(t.devicesTotal),
        money2(t.stationeryTotal),
        money2(t.approvedValue),
      ],
      { fill: totalFill, ink: totalInk, bold: true }
    );
  }

  const r = input.report;
  if (r === "shelters" || r === "all") drawShelterReport();
  if (r === "children" || r === "all") drawCategoryReport();
  if (r === "users" || r === "all") drawUsersReport();
  if (r === "requests" || r === "all") drawRequestsReport();
  if (r === "no_login" || r === "all") drawNoLoginReport();
  if (r === "statistics") drawStatisticsReport();

  // ── Footer: generated timestamp + page numbers ──
  const pageCount = doc.getNumberOfPages();
  for (let p = 1; p <= pageCount; p++) {
    doc.setPage(p);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    setInk(muted);
    doc.text(`Generated ${new Date().toLocaleString("en-AU")}`, margin, 292);
    doc.text(`Page ${p} of ${pageCount}`, margin + contentW, 292, { align: "right" });
  }

  // ── Output: open in a new tab, download fallback if the popup is blocked ──
  try {
    const blob = doc.output("blob");
    const url = URL.createObjectURL(blob);
    const win = window.open(url, "_blank");
    if (!win) {
      doc.save(`${reportTitle(input.report)}.pdf`);
      URL.revokeObjectURL(url);
    } else {
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    }
    return true;
  } catch (e) {
    logger.warn("Standard Report PDF open failed:", e);
    return false;
  }
}
